<template>
  <BaseLayout>
    <template #header>
      <AppVersion />
      <StageCatalogueWarning v-if="usesStageCatalogue" />
      <TrustpilotHeader />
      <AppHeader
        :categories="firstLevelCategories"
        :product="product"
      />
      <PromoBannerContainer />
      <NavigationWrapper
        :categories-container="categoriesContainer"
        :top-level-domain="topLevelDomain"
        :language-and-country="languageAndCountry"
      />
    </template>
    <NewsletterSubscriptionWidget />
    <slot />
    <template #content>
      <ComplementaryProductsSidebar
      />
      <CartSidebar
      />
      <NotificationToastWrapper
      />
      <PartnersTransitionBanner />
      <ConfirmationPopup v-if="isConfirmationPopupOpen" />
    </template>
  </BaseLayout>
</template>
  
<script setup lang="ts">
import type { Category } from '~~/types/commercetools';
import BaseLayout from '~/layouts/base-layout.vue';
import type { ProductVariantExtended } from '~~/types/product/ProductVariantExtended';
import { isProduction } from '~~/helpers/env/checkEnvironmentType';

const NavigationWrapper = defineAsyncComponent(() =>
  import(/* webpackChunkName: "NavigationWrapper" */
    '~/components/organisms/Navigation/NavigationWrapper.vue'));
const AppVersion = defineAsyncComponent(() =>
  import(/* webpackChunkName: "AppVersion" */
    '~/components/molecules/AppVersion/AppVersion.vue'));
const CartSidebar = defineAsyncComponent(() =>
  import(/* webpackChunkName: "CartSidebar" */
    '~/components/organisms/Cart/CartSidebar.vue'));
const ComplementaryProductsSidebar = defineAsyncComponent(() =>
  import(/* webpackChunkName: "ComplementaryProductsSidebar" */
    '~/components/organisms/ComplementaryProducts/ComplementaryProductsSidebar/ComplementaryProductsSidebar.vue'));
const NotificationToastWrapper = defineAsyncComponent(() =>
  import(/* webpackChunkName: "NotificationToastWrapper" */
    '~/components/molecules/NotificationToastWrapper.vue'));
const PartnersTransitionBanner = defineAsyncComponent(() =>
  import(/* webpackChunkName: "PartnersTransitionBanner" */
    '~/components/molecules/PartnersTransitionBanner/PartnersTransitionBanner.vue'));
const ConfirmationPopup = defineAsyncComponent(() =>
  import(/* webpackChunkName: "ConfirmationPopup" */
    '~/components/molecules/ConfirmationPopup/ConfirmationPopup.vue')); 
const StageCatalogueWarning = defineAsyncComponent(() =>
  import(/* webpackChunkName: "StageCatalogueWarning" */
    '~/components/molecules/StageCatalogueWarning/StageCatalogueWarning.vue'));
type Props = {
  fullWidth?: boolean;
  categoriesContainer?: Category[];
  product?: ProductVariantExtended | null;
};

const props = defineProps<Props>();

const categoriesContainer = computed<Category[]>(() => props.categoriesContainer ?? []);
const product = computed<ProductVariantExtended | null>(() => props.product ?? null);

const { isConfirmationPopupOpen, checkIfShouldShowConfirmationPopup } = useConfirmationPopup();

const { countryLocalization, languageAndCountry } = useLocalization();

const firstLevelCategories = computed(() => {

  return categoriesContainer.value?.[0]?.children || [];

});

const topLevelDomain = countryLocalization.value.topLevelDomain;

const { isCtProductSearchEnabled } = useIsCtProductSearchEnabled();
const usesStageCatalogue = !isProduction()
  && (isCtProductSearchEnabled.value || Boolean(useRuntimeConfig().public.TYPESENSE_HOST));

onMounted(() => {

  checkIfShouldShowConfirmationPopup();

});
</script>

<style lang="scss">
.no-scroll {
  overflow: hidden;
  height: 100vh;
}

// Reset CSS
html {
  width: auto;
}

body {
  overflow-x: hidden;
  color: var(--c-text);
  font-size: var(--font-size--base);
  font-family: var(--font-family--primary);
}

ul {
  list-style-type: disc;
}

ol {
  list-style-type: decimal;
}

a {
  line-height: var(--body-line-height);
  cursor: pointer;
}

/** Function for generating h1-h6 headings */
@for $i from 1 through 6 {
  h#{$i} {
    font-size: var(--h#{$i}-font-size);
    font-weight: var(--h#{$i}-font-weight);
    line-height: var(--h#{$i}-line-height);
  }
}

.sf-input__label {
  cursor: text;
}
</style>
